// FILE: src/features/profileSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import fetchWithAuth from "../utils/fetchWithAuth";

// fetchProfile => GET /api/profile
export const fetchProfile = createAsyncThunk(
  "profile/fetchProfile",
  async (_, { rejectWithValue }) => {
    try {
      // GET /api/profile
      const data = await fetchWithAuth("/api/profile");
      // Suppose the response is { phone, licenseClass, accountLocked, daysLeft, ... }
      return data;
    } catch (err) {
      return rejectWithValue(err.message || "Failed to fetch profile");
    }
  }
);

// updateProfile => PATCH /api/profile. Students may change phone and
// address (and give consent); legal name and licence are support-only
// (FIELD_NOT_EDITABLE). Pass a plain object (sent as JSON) or FormData
// (licence copy upload). Resolves to { message, profile }.
export const updateProfile = createAsyncThunk(
  "profile/updateProfile",
  async (updates, { rejectWithValue }) => {
    try {
      const body = updates instanceof FormData ? updates : JSON.stringify(updates);
      return await fetchWithAuth("/api/profile", { method: "PATCH", body });
    } catch (err) {
      // Keep the server body (code + problems) so forms can show field errors.
      return rejectWithValue(err.body || { code: err.code || err.message });
    }
  }
);

// 1) New thunk: updateAddressAndConsent
export const updateAddressAndConsent = createAsyncThunk(
  "profile/updateAddressAndConsent",
  async (payload, { rejectWithValue }) => {
    try {
      // e.g. payload = { address: "...", hasConsent: true/false }
      const response = await fetchWithAuth("/api/profile", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      // Suppose the server returns updated fields or the whole updated profile
      // e.g. { address: "...", hasConsent: true, ... }
      return response;
    } catch (err) {
      // If 400, 403, or other error
      return rejectWithValue(err.message || "Failed to update address/consent");
    }
  }
);

const profileSlice = createSlice({
  name: "profile",
  initialState: {
    profile: null,
    loading: false,
    error: null
  },
  reducers: {
    clearProfile(state) {
      state.profile = null;
      state.loading = false;
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    // fetchProfile
    builder
      .addCase(fetchProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.profile = action.payload; // e.g. { phone, licenseClass, accountLocked, daysLeft, ... }
      })
      .addCase(fetchProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error fetching profile";
      });

    // updateProfile: the page shows its own saving state and errors, so the
    // loaded profile stays on screen (no global loading/error flip).
    builder.addCase(updateProfile.fulfilled, (state, action) => {
      const next = action.payload?.profile;
      if (next) state.profile = { ...(state.profile || {}), ...next };
    });

    // updateAddressAndConsent: saving shouldn't look like (or fail like) a
    // profile load; the page keeps its own saving state.
    builder.addCase(updateAddressAndConsent.fulfilled, (state, action) => {
      if (action.payload?.profile) state.profile = action.payload.profile;
    });
  }
});

export const { clearProfile } = profileSlice.actions;
export default profileSlice.reducer;
